/**
 * 이 브라우저의 목업 세션 (PRD2 T-1.2).
 * 비밀번호는 두지 않는다. 로컬 플랜과는 별개다.
 */

import { useSyncExternalStore } from "react";

export type SignInProvider = "microsoft" | "google" | "email";

export type Session = {
  email: string;
  provider: SignInProvider;
  signedInAt: string;
};

const KEY = "wavex-plan-session";
const MOCK_EMAIL = "me@wavex.local";

let session: Session | null = readStored();
const listeners = new Set<() => void>();

function readStored(): Session | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session;
    if (!parsed.email || !parsed.provider) return null;
    return { email: parsed.email, provider: parsed.provider, signedInAt: parsed.signedInAt };
  } catch {
    return null;
  }
}

function emit() {
  for (const listener of listeners) listener();
}

function write(next: Session | null) {
  session = next;
  if (typeof localStorage !== "undefined") {
    if (next) localStorage.setItem(KEY, JSON.stringify(next));
    else localStorage.removeItem(KEY);
  }
  emit();
}

export function resetSession(): void {
  write(null);
}

export function getSession(): Session | null {
  return session;
}

export function subscribeSession(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useSession(): Session | null {
  return useSyncExternalStore(subscribeSession, getSession, getSession);
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** 이메일은 @가 있어야 한다. Microsoft·Google은 회사 계정 자리만 채운다. */
export function signIn(provider: SignInProvider, email = ""): Session | null {
  const trimmed = email.trim().toLowerCase();
  const nextEmail = provider === "email" ? trimmed : trimmed || MOCK_EMAIL;
  if (!EMAIL.test(nextEmail)) return null;
  const next: Session = { email: nextEmail, provider, signedInAt: new Date().toISOString() };
  write(next);
  return next;
}

export function signOut(): void {
  write(null);
}

/** 로그인 뒤 이동은 앱 안 초대 경로와 홈만 허용한다. */
export function safeAppReturn(value: string | null): string {
  if (!value || value.includes("://") || value.includes("\\") || value.includes("..")) return "/";
  if (value === "/") return "/";
  if (/^\/i\/[^/]+$/.test(value)) return value;
  if (/^\/invite\/t\/[^/]+$/.test(value)) return value;
  return "/";
}

/** 초대 화면에서 로그인으로 돌아갈 주소. 허용된 경로만 next에 싣는다. */
export function inviteLoginHref(returnPath: string): string {
  const next = safeAppReturn(returnPath);
  if (next === "/") return "/login";
  return `/login?next=${encodeURIComponent(next)}`;
}
